import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, DoctorSelectedEvent } from '@theme/events';
import { doctorList } from '@theme/doctor-list';

const OTHER_VALUE = '__other__';

/**
 * A custom element that lets the customer pick a doctor from a fixed list (or type in a
 * name via an "Other" option), shows the doctor's hospital/city/profession for
 * verification, and persists only the doctor's name to the cart's `Doctor Name` attribute.
 *
 * Note: all of this element's interactive children live inside a nested `<dialog-component>`
 * (itself a `Component`), which acts as a ref-collection boundary — so this class deliberately
 * does NOT use `this.refs` and instead queries its children directly, the same way
 * `assets/local-pickup.js` avoids `this.refs` for content inside its own nested dialog.
 *
 * @extends Component
 */
class DoctorSelectComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#populateOptions();
    document.addEventListener(ThemeEvents.doctorSelected, this.#handleGlobalDoctorSelected);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.doctorSelected, this.#handleGlobalDoctorSelected);
  }

  /** @returns {HTMLSelectElement | null} */
  get #doctorSelect() {
    return this.querySelector('[ref="doctorSelect"]');
  }

  /** @returns {HTMLInputElement | null} */
  get #otherNameInput() {
    return this.querySelector('[ref="otherNameInput"]');
  }

  /** @returns {HTMLButtonElement | null} */
  get #confirmButton() {
    return this.querySelector('[ref="confirmButton"]');
  }

  /** @returns {HTMLElement | null} */
  get #triggerLabel() {
    return this.querySelector('[ref="triggerLabel"]');
  }

  #populateOptions() {
    const doctorSelect = this.#doctorSelect;
    if (!doctorSelect) return;

    const currentName = this.dataset.selectedDoctor ?? '';
    let matchedKnownDoctor = false;

    for (const doctor of doctorList) {
      const option = document.createElement('option');
      option.value = doctor.name;
      option.textContent = doctor.name;
      if (doctor.name === currentName) {
        option.selected = true;
        matchedKnownDoctor = true;
      }
      doctorSelect.append(option);
    }

    const otherOption = document.createElement('option');
    otherOption.value = OTHER_VALUE;
    otherOption.textContent = Theme.translations.doctorOtherOption;
    doctorSelect.append(otherOption);

    if (currentName && !matchedKnownDoctor) {
      // A previously saved name that isn't in the fixed list — must have come from "Other".
      otherOption.selected = true;
      const otherNameInput = this.#otherNameInput;
      if (otherNameInput) otherNameInput.value = currentName;
    }

    if (currentName) this.#updateSelectionUI(matchedKnownDoctor ? currentName : OTHER_VALUE);
  }

  /**
   * Handles a change to the doctor `<select>`.
   * @param {Event} event
   */
  handleDoctorChange(event) {
    const target = /** @type {HTMLSelectElement} */ (event.target);
    this.#updateSelectionUI(target.value);
  }

  /**
   * Handles typing in the "Other" name input.
   * @param {Event} event
   */
  handleOtherNameInput(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    const confirmButton = this.#confirmButton;
    const doctorError = this.querySelector('[ref="doctorError"]');

    doctorError?.setAttribute('hidden', '');
    if (confirmButton) confirmButton.disabled = target.value.trim() === '';
  }

  /** @param {string} value - The current `<select>` value (a doctor name, `OTHER_VALUE`, or empty). */
  #updateSelectionUI(value) {
    const doctorDetails = this.querySelector('[ref="doctorDetails"]');
    const doctorHospital = this.querySelector('[ref="doctorHospital"]');
    const doctorCity = this.querySelector('[ref="doctorCity"]');
    const doctorProfession = this.querySelector('[ref="doctorProfession"]');
    const otherNameWrapper = this.querySelector('[ref="otherNameWrapper"]');
    const otherNameInput = this.#otherNameInput;
    const doctorError = this.querySelector('[ref="doctorError"]');
    const confirmButton = this.#confirmButton;

    doctorError?.setAttribute('hidden', '');

    if (value === OTHER_VALUE) {
      doctorDetails?.setAttribute('hidden', '');
      otherNameWrapper?.removeAttribute('hidden');
      if (confirmButton) confirmButton.disabled = (otherNameInput?.value.trim() ?? '') === '';
      return;
    }

    otherNameWrapper?.setAttribute('hidden', '');

    const doctor = doctorList.find((entry) => entry.name === value);

    if (!doctor) {
      doctorDetails?.setAttribute('hidden', '');
      if (confirmButton) confirmButton.disabled = true;
      return;
    }

    if (doctorHospital) doctorHospital.textContent = doctor.hospital;
    if (doctorCity) doctorCity.textContent = doctor.city;
    if (doctorProfession) doctorProfession.textContent = doctor.profession;
    doctorDetails?.removeAttribute('hidden');
    if (confirmButton) confirmButton.disabled = false;
  }

  async confirmDoctor() {
    const doctorSelect = this.#doctorSelect;
    const otherNameInput = this.#otherNameInput;
    const confirmButton = this.#confirmButton;
    const triggerLabel = this.#triggerLabel;
    const doctorError = this.querySelector('[ref="doctorError"]');

    const name =
      doctorSelect?.value === OTHER_VALUE ? (otherNameInput?.value ?? '').trim() : (doctorSelect?.value ?? '');
    if (!name || !confirmButton) return;

    confirmButton.disabled = true;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ attributes: { 'Doctor Name': name } }),
      });

      const response = await fetch(Theme.routes.cart_update_url, config);
      if (!response.ok) throw new Error('Failed to update cart attributes');

      const updatedCart = await response.json();
      if (updatedCart.attributes?.['Doctor Name'] !== name) {
        throw new Error('Doctor Name was not saved to the cart');
      }

      this.dataset.selectedDoctor = name;
      if (triggerLabel) triggerLabel.textContent = Theme.translations.doctorSelectedLabel.replace('[name]', name);

      document.dispatchEvent(new DoctorSelectedEvent(name));

      const dialogComponent = this.querySelector('dialog-component');
      if (dialogComponent && typeof (/** @type {any} */ (dialogComponent).closeDialog) === 'function') {
        /** @type {any} */ (dialogComponent).closeDialog();
      }
    } catch (error) {
      doctorError?.removeAttribute('hidden');
    } finally {
      confirmButton.disabled = false;
    }
  }

  /** @param {CustomEvent<{doctorName: string}>} event */
  #handleGlobalDoctorSelected = (event) => {
    const { doctorName } = event.detail;
    const doctorSelect = this.#doctorSelect;
    const triggerLabel = this.#triggerLabel;

    this.dataset.selectedDoctor = doctorName;
    if (doctorSelect) {
      const matchesKnownOption = [...doctorSelect.options].some((option) => option.value === doctorName);
      doctorSelect.value = matchesKnownOption ? doctorName : OTHER_VALUE;
      if (!matchesKnownOption) {
        const otherNameInput = this.#otherNameInput;
        if (otherNameInput) otherNameInput.value = doctorName;
      }
    }
    if (triggerLabel) triggerLabel.textContent = Theme.translations.doctorSelectedLabel.replace('[name]', doctorName);
  };
}

if (!customElements.get('doctor-select-component')) {
  customElements.define('doctor-select-component', DoctorSelectComponent);
}
